import React, { useState, useEffect, useCallback } from 'react';
import CondoCard from '../components/CondoCard';
import StatsBar from '../components/StatsBar';
import SearchBar from '../components/SearchBar';
import { CONDOS, STATS } from '../data/mockData';

const NEIGHBORHOODS = ['All', 'Brickell', 'Edgewater', 'South Beach', 'Wynwood', 'Coconut Grove'];
const RISKS = ['All', 'low', 'medium', 'high'];

function riskLabel(r) {
  return r === 'low' ? 'Low Risk' : r === 'medium' ? 'Medium Risk' : r === 'high' ? 'High Risk' : 'All';
}

export default function Home({ onSelect }) {
  const [hood, setHood] = useState('All');
  const [risk, setRisk] = useState('All');
  const [q, setQ] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const filtered = CONDOS.filter(c => {
    if (hood !== 'All' && c.neighborhood !== hood) return false;
    if (risk !== 'All') {
      const score = c.risk_score;
      if (risk === 'low' && score >= 40) return false;
      if (risk === 'medium' && (score < 40 || score >= 70)) return false;
      if (risk === 'high' && score < 70) return false;
    }
    if (q && !c.name.toLowerCase().includes(q.toLowerCase()) && !c.address.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Nav />
      <Hero mounted={mounted} />
      <StatsBar stats={STATS} />

      {/* Filters */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 48px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 32, animation: mounted ? 'fadeUp 0.5s 0.3s both' : 'none' }}>
          <SearchBar value={q} onChange={setQ} />
          <FilterGroup label="Neighborhood" options={NEIGHBORHOODS} value={hood} onChange={setHood} getLabel={x => x} />
          <FilterGroup label="Risk Level" options={RISKS} value={risk} onChange={setRisk} getLabel={riskLabel} />
        </div>

        {/* Results header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20, animation: mounted ? 'fadeUp 0.5s 0.35s both' : 'none' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, letterSpacing: 0.2 }}>
            {filtered.length} {filtered.length === 1 ? 'property' : 'properties'}
          </span>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
            Data sourced from Florida DBPR · Updated weekly
          </span>
        </div>

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 16,
        }}>
          {filtered.map((condo, i) => (
            <div key={condo.id} style={{ animation: mounted ? `fadeUp 0.45s ${0.05 * i + 0.4}s both` : 'none' }}>
              <CondoCard condo={condo} onClick={() => onSelect(condo.id)} />
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', color: 'var(--text-tertiary)' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>No properties match your filters</div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'rgba(10,10,15,0.85)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderBottom: '1px solid var(--border-subtle)',
      padding: '0 24px',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo size={28} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--text-primary)', letterSpacing: -0.3 }}>
            CondoCheck
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <NavPill>Miami–Dade</NavPill>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
            color: 'var(--risk-high)', background: 'var(--risk-high-bg)',
            border: '1px solid var(--risk-high-border)',
            padding: '3px 10px', borderRadius: 100,
          }}>
            Beta
          </div>
        </div>
      </div>
    </nav>
  );
}

function NavPill({ children }) {
  return (
    <span style={{
      fontSize: 12, color: 'var(--text-secondary)',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      padding: '4px 12px', borderRadius: 100,
    }}>
      {children}
    </span>
  );
}

function Hero({ mounted }) {
  return (
    <div style={{
      maxWidth: 1200, margin: '0 auto', padding: '72px 24px 56px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 400,
        background: 'radial-gradient(ellipse at center, rgba(10,132,255,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', maxWidth: 680 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 100, padding: '6px 14px', marginBottom: 24,
          animation: mounted ? 'fadeUp 0.5s 0.05s both' : 'none',
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--risk-low)', boxShadow: '0 0 0 2px var(--risk-low-bg)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', letterSpacing: 0.3 }}>
            Florida DBPR · Condominium Public Records
          </span>
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(36px, 5vw, 58px)',
          lineHeight: 1.1, letterSpacing: -1,
          color: 'var(--text-primary)',
          marginBottom: 20,
          animation: mounted ? 'fadeUp 0.5s 0.1s both' : 'none',
        }}>
          Know before you<br />
          <span style={{ fontStyle: 'italic', color: 'var(--accent)' }}>buy.</span>
        </h1>

        <p style={{
          fontSize: 17, color: 'var(--text-secondary)', lineHeight: 1.6,
          maxWidth: 520, fontWeight: 300,
          animation: mounted ? 'fadeUp 0.5s 0.15s both' : 'none',
        }}>
          CondoCheck surfaces complaint history, financial health, and risk signals for Miami–Dade condominiums — sourced directly from Florida DBPR public records.
        </p>
      </div>
    </div>
  );
}

function FilterGroup({ label, options, value, onChange, getLabel }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500, marginRight: 2 }}>{label}:</span>
      {options.map(o => (
        <button key={o} onClick={() => onChange(o)} style={{
          fontSize: 12, fontWeight: 500,
          padding: '5px 12px', borderRadius: 100, cursor: 'pointer',
          border: value === o ? '1px solid var(--accent)' : '1px solid var(--border)',
          background: value === o ? 'var(--accent-glow)' : 'var(--bg-card)',
          color: value === o ? 'var(--accent)' : 'var(--text-secondary)',
          transition: 'all var(--transition)',
          fontFamily: 'var(--font-body)',
        }}>
          {getLabel(o)}
        </button>
      ))}
    </div>
  );
}

function Footer() {
  return (
    <footer style={{
      borderTop: '1px solid var(--border-subtle)',
      padding: '32px 24px',
      textAlign: 'center',
    }}>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.8 }}>
        CondoCheck is an independent tool for informational purposes only.<br />
        Data sourced from Florida DBPR public records. Not legal or financial advice.<br />
        <span style={{ color: 'var(--text-tertiary)', opacity: 0.6 }}>© 2025 CondoCheck · Miami, FL</span>
      </p>
    </footer>
  );
}

function Logo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#0A84FF" fillOpacity="0.15" />
      <rect x="0.5" y="0.5" width="31" height="31" rx="7.5" stroke="#0A84FF" strokeOpacity="0.3" />
      <path d="M8 24V14l8-6 8 6v10" stroke="#0A84FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="13" y="17" width="6" height="7" rx="1" stroke="#0A84FF" strokeWidth="1.5"/>
    </svg>
  );
}
